import { Box, Card, TextField, Typography } from "@mui/material"
import { AlignLeft } from "lucide-react"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { BROCHURE_COLORS, BROCHURE_FONT, escapeHtml, type UseCaseSection } from "./model"

// Editor for the challenge/intro: one heading + one balanced lead paragraph.
interface ChallengeSectionProps {
  section: UseCaseSection
  onUpdate: (sectionId: string, partial: any) => void
}

export function ChallengeSection({ section, onUpdate }: ChallengeSectionProps) {
  const c = section.content
  return (
    <Card sx={{ borderRadius: 0 }} style={{ marginBottom: 0 }}>
      <Box sx={{ p: 3 }}>
        <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <AlignLeft size={20} />
          The challenge
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`uc-ch-heading-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Heading</Typography>
              <AiTextImprover fieldType="short" context="use-case brochure challenge heading" currentText={c.heading} onImprove={(v) => onUpdate(section.id, { heading: v })} />
            </Box>
            <TextField
              fullWidth size="small" id={`uc-ch-heading-${section.id}`}
              value={c.heading || ""}
              onChange={(e) => onUpdate(section.id, { heading: e.target.value })}
              placeholder="e.g., When the alarm sounds, the room phone isn't enough"
            />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`uc-ch-body-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Body</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="long"
                  context="use-case brochure challenge paragraph — one full, balanced paragraph that frames the problem"
                  placeholder="e.g., describe the problem"
                  currentValue={c.body}
                  onGenerate={(v) => onUpdate(section.id, { body: v })}
                />
                <AiTextImprover fieldType="long" context="use-case brochure challenge paragraph" currentText={c.body} onImprove={(v) => onUpdate(section.id, { body: v })} />
              </Box>
            </Box>
            <TextField
              fullWidth multiline minRows={4} size="small" id={`uc-ch-body-${section.id}`}
              value={c.body || ""}
              onChange={(e) => onUpdate(section.id, { body: e.target.value })}
              placeholder="One balanced paragraph framing the challenge"
            />
          </Box>
        </Box>
      </Box>
    </Card>
  )
}

// Serialize the challenge: one heading + the body, full width and left-aligned.
// The body may hold MULTIPLE paragraphs — separate them with a blank line in the
// editor and each becomes its own <p> (so the full copy is preserved, not
// squeezed into one block).
export function challengeToHtml(section: UseCaseSection): string {
  const c = section.content || {}
  const heading = c.heading ? escapeHtml(c.heading) : ""
  const paragraphs = String(c.body ?? "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
  if (!heading && paragraphs.length === 0) return ""

  const body = paragraphs
    .map(
      (p, i) => `<p style="margin: ${i === 0 ? '0' : '14px'} 0 0 0; color: ${BROCHURE_COLORS.ink}; font-family: ${BROCHURE_FONT}; font-size: 16px; line-height: 26px;">${escapeHtml(p)}</p>`,
    )
    .join("")

  return `
          <!-- Challenge -->
          <tr>
            <td bgcolor="${BROCHURE_COLORS.card}" class="mobile-pad" style="background-color: ${BROCHURE_COLORS.card}; padding: 40px 40px 20px 40px;">
              ${heading ? `<h2 style="margin: 0 0 14px 0; color: ${BROCHURE_COLORS.navy}; font-family: ${BROCHURE_FONT}; font-size: 24px; line-height: 30px; font-weight: bold;">${heading}</h2>` : ''}
              ${body}
            </td>
          </tr>
          `
}
